"use client";

import { useEffect, useRef } from "react";
import { useAnalytics } from "@/lib/analytics/hooks";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { SaveSearchButton } from "./save-search-button";

export type ZeroResultAlertChip = { label: string; href: string };

type Props = {
	locale: string;
	/** The exact query the zero-result search was rendered for — never empty
	 * (the caller only renders this when a `q` term produced zero rows). */
	query: string;
	/** Up to 3 suggestion chips: the query's derived category page (when one
	 * was derivable) plus top global zero-result queries. */
	chips: ZeroResultAlertChip[];
	t?: Dictionary;
};

/**
 * PR-H: a zero-result search is logged
 * (`search_results_rendered.is_zero_result`) but nobody acted on it — the
 * highest-intent moment to capture an email alert. Reuses SaveSearchButton's
 * existing save-search → daily new-jobs-alerts-cron flow (same pattern as
 * RepeatSearchNudge) instead of a new form: SaveSearchButton reads `q`
 * straight off the URL, so the alert is prefilled with this exact query with
 * no extra plumbing.
 */
export function ZeroResultAlert({ locale, query, chips, t }: Props) {
	const { posthog } = useAnalytics();
	const shownLogged = useRef(false);
	const isEn = locale === "en";

	useEffect(() => {
		if (shownLogged.current) return;
		shownLogged.current = true;
		posthog.capture("zero_result_alert_shown", {
			locale,
			search_query: query,
			chip_count: chips.length,
		});
		// Fire once per mount (a new query is a new empty-state render, same
		// granularity RepeatSearchNudge uses for `search_nudge_shown`).
	}, [posthog, locale, query, chips.length]);

	const handleSaved = () => {
		posthog.capture("zero_result_alert_saved", { locale, search_query: query });
	};

	const fallback = isEn
		? `No jobs for "${query}" yet. Alert me when there are.`
		: `Belum ada lowongan untuk "${query}". Kabari saya saat ada.`;
	const message = t?.zeroResultAlertMessage
		? t.zeroResultAlertMessage.replace("{q}", query)
		: fallback;

	return (
		<div
			className="mt-2 flex flex-col items-center gap-3 rounded-2xl border border-primary/20 bg-primary/[0.04] px-4 py-6 text-center"
			data-testid="zero-result-alert"
		>
			<p className="text-base font-semibold text-foreground">{message}</p>
			<SaveSearchButton
				locale={locale}
				labels={{
					save: isEn ? "Alert me" : "Kabari saya",
					saved: isEn ? "Saved" : "Tersimpan",
				}}
				onSaved={handleSaved}
			/>
			{chips.length > 0 && (
				<div className="mt-1 flex flex-wrap justify-center gap-2">
					{chips.map((chip) => (
						<a
							key={chip.href}
							href={chip.href}
							className="rounded-full border border-border/60 bg-card/70 min-h-11 px-3.5 py-1.5 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
						>
							{chip.label}
						</a>
					))}
				</div>
			)}
		</div>
	);
}
